import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphConfig, getConfig } from '../authConfig';
import { apiFetch } from '../lib/apiFetch';

interface UserProfile {
  displayName: string;
  userPrincipalName: string;
  photoUrl?: string;
}

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
});

export const useAuth = () => useContext(AuthContext);

// DisabledAuthProvider removed. SSO/OIDC disable logic is no longer needed.

const MsalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastTokenStoreAccountId = useRef<string | null>(null);

  useEffect(() => {
    const storeBackendToken = async () => {
      if (accounts.length === 0) {
        lastTokenStoreAccountId.current = null;
        return;
      }

      const account = accounts[0];
      if (lastTokenStoreAccountId.current === account.homeAccountId) {
        return;
      }

      const config = getConfig();
      const backendRequiresAuth = config.BACKEND_REQUIRES_AUTH === true || config.BACKEND_REQUIRES_AUTH === 'true';
      const enableBackendTokenCache = config.ENABLE_BACKEND_TOKEN_CACHE === true || config.ENABLE_BACKEND_TOKEN_CACHE === 'true';
      if (!backendRequiresAuth) {
        return;
      }

      if (!enableBackendTokenCache) {
        return;
      }

      const baseUrl = (window as any).ENV_CONFIG?.API_BASE_URL
        ? `${(window as any).ENV_CONFIG.API_BASE_URL}/api/servicenow/basic`
        : 'http://localhost:5000/api/servicenow/basic';

      try {
        const response = await apiFetch(`${baseUrl}/token/store`, { method: 'POST' });
        if (response.ok) {
          lastTokenStoreAccountId.current = account.homeAccountId;
        } else if (response.status === 401) {
          // Non-blocking: main API calls can still work with per-request token acquisition.
          console.debug('Backend token cache endpoint returned 401; continuing without cached backend token');
        } else {
          console.warn('Failed to store backend token:', response.status, response.statusText);
        }
      } catch (error) {
        console.warn('Error storing backend token:', error);
      }
    };

    storeBackendToken();
  }, [accounts]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (accounts.length > 0) {
        const account = accounts[0];
        
        try {
          // Keep profile loading non-interactive to avoid popup loops.
          const response = await instance.acquireTokenSilent({
            scopes: ['User.Read'],
            account: account,
          });
          const accessToken = response.accessToken;

          // Fetch user profile
          const profileResponse = await fetch(graphConfig.graphMeEndpoint, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          
          if (!profileResponse.ok) {
            throw new Error(`Failed to fetch profile: ${profileResponse.status}`);
          }
          
          const profileData = await profileResponse.json();

          // Fetch user photo
          let photoUrl: string | undefined;
          try {
            const photoResponse = await fetch(graphConfig.graphPhotoEndpoint, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
            
            if (photoResponse.ok) {
              const photoBlob = await photoResponse.blob();
              photoUrl = URL.createObjectURL(photoBlob);
            } else if (photoResponse.status !== 404) {
              console.warn('Photo fetch failed with status:', photoResponse.status);
            }
          } catch (error) {
            console.warn('Could not fetch profile photo:', error);
          }

          setUser({
            displayName: profileData.displayName || account.name || 'User',
            userPrincipalName: profileData.userPrincipalName || account.username,
            photoUrl,
          });
        } catch (error) {
          console.warn('Profile token acquisition failed silently; using account fallback', error);
          // Fallback to account info
          setUser({
            displayName: account.name || 'User',
            userPrincipalName: account.username,
          });
        }
      }
      setIsLoading(false);
    };

    fetchUserProfile();
  }, [accounts, instance]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: accounts.length > 0,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
};
